/**
 * Mock Support Data
 *
 * Mock data for support tickets, messages, knowledge base articles, and FAQs.
 * Sprint 1: Used directly by mock API
 * Sprint 2+: Replace with real backend data
 */

import type {
  SupportTicket,
  TicketMessage,
  KnowledgeBaseArticle,
  FAQ,
  SupportStats,
  TicketType,
  TicketStatus,
  TicketPriority,
  KnowledgeBaseCategory,
} from '../types/support';

// ===== Support Tickets =====

export const mockTickets: SupportTicket[] = [
  {
    id: 'ticket-001',
    ticketNumber: 'TKT-2026-00001',
    companyId: 'company-001',
    companyName: 'Acme Food Products Ltd',
    type: 'application_support' as TicketType,
    status: 'in_progress' as TicketStatus,
    priority: 'high' as TicketPriority,
    subject: 'Unable to upload supplier documents',
    description:
      'I am trying to upload our halal certificate for supplier ABC Corp, but the file upload keeps failing with error "File too large". The file is only 2MB. Can you help?',
    assignedTo: 'staff-001',
    assignedToName: 'Sarah Johnson',
    assignedToRole: 'consultant',
    assignedAt: new Date('2026-01-03T10:30:00'),
    relatedApplicationId: 'app-001',
    createdBy: 'user-001',
    createdByName: 'John Smith',
    createdAt: new Date('2026-01-03T09:15:00'),
    updatedAt: new Date('2026-01-03T14:20:00'),
    tags: ['file-upload', 'supplier-documents'],
    attachments: [],
    messageCount: 3,
    slaDeadline: new Date('2026-01-03T17:15:00'),
    responseDeadline: new Date('2026-01-03T17:15:00'),
    firstResponseAt: new Date('2026-01-03T10:30:00'),
  },
  {
    id: 'ticket-002',
    ticketNumber: 'TKT-2026-00002',
    companyId: 'company-002',
    companyName: 'Global Halal Foods Inc',
    type: 'audit_question' as TicketType,
    status: 'waiting_customer' as TicketStatus,
    priority: 'medium' as TicketPriority,
    subject: 'Pre-audit checklist clarification needed',
    description:
      'Regarding item #12 on the pre-audit checklist about ingredient sourcing documentation - do we need certificates for every single ingredient or just animal-derived ones?',
    assignedTo: 'staff-002',
    assignedToName: 'Ahmed Al-Rahman',
    assignedToRole: 'consultant',
    assignedAt: new Date('2026-01-02T11:00:00'),
    createdBy: 'user-003',
    createdByName: 'Maria Garcia',
    createdAt: new Date('2026-01-02T10:45:00'),
    updatedAt: new Date('2026-01-02T15:30:00'),
    tags: ['pre-audit', 'documentation'],
    attachments: [],
    messageCount: 2,
    slaDeadline: new Date('2026-01-03T10:45:00'),
    responseDeadline: new Date('2026-01-03T10:45:00'),
    firstResponseAt: new Date('2026-01-02T14:20:00'),
  },
  {
    id: 'ticket-003',
    ticketNumber: 'TKT-2026-00003',
    companyId: 'company-003',
    companyName: 'Fresh Bakery Co',
    type: 'training_support' as TicketType,
    status: 'resolved' as TicketStatus,
    priority: 'low' as TicketPriority,
    subject: 'Training session recording access',
    description:
      'I missed the December 15 training session on Halal Standards. Is there a recording available?',
    assignedTo: 'staff-003',
    assignedToName: 'Linda Chen',
    assignedToRole: 'trainer',
    assignedAt: new Date('2025-12-20T09:00:00'),
    createdBy: 'user-005',
    createdByName: 'David Lee',
    createdAt: new Date('2025-12-20T08:30:00'),
    updatedAt: new Date('2025-12-20T10:15:00'),
    resolvedAt: new Date('2025-12-20T10:15:00'),
    tags: ['training', 'recording-access'],
    attachments: [],
    messageCount: 2,
    slaDeadline: new Date('2025-12-22T08:30:00'),
    responseDeadline: new Date('2025-12-22T08:30:00'),
    firstResponseAt: new Date('2025-12-20T09:10:00'),
    rating: 5,
    ratingFeedback: 'Very helpful! Got the recording link within an hour.',
  },
  {
    id: 'ticket-004',
    ticketNumber: 'TKT-2026-00004',
    companyId: 'company-001',
    companyName: 'Acme Food Products Ltd',
    type: 'payment_issue' as TicketType,
    status: 'open' as TicketStatus,
    priority: 'urgent' as TicketPriority,
    subject: 'Payment not reflected after bank transfer',
    description:
      'We made a bank transfer for invoice INV-2025-001 on December 28, but the payment status still shows as pending. Our reference number is BT-20251228-4567.',
    createdBy: 'user-001',
    createdByName: 'John Smith',
    createdAt: new Date('2026-01-04T08:00:00'),
    updatedAt: new Date('2026-01-04T08:00:00'),
    relatedInvoiceId: 'inv-001',
    tags: ['payment', 'bank-transfer'],
    attachments: [],
    messageCount: 0,
    slaDeadline: new Date('2026-01-04T10:00:00'),
    responseDeadline: new Date('2026-01-04T10:00:00'),
  },
  {
    id: 'ticket-005',
    ticketNumber: 'TKT-2026-00005',
    companyId: 'company-004',
    companyName: 'Halal Restaurant Chain',
    type: 'certificate_request' as TicketType,
    status: 'in_progress' as TicketStatus,
    priority: 'medium' as TicketPriority,
    subject: 'Need additional certificate copies',
    description:
      'We need 3 more physical copies of our halal certificate (CERT-2025-001) to display in our new branch locations.',
    assignedTo: 'staff-001',
    assignedToName: 'Sarah Johnson',
    assignedToRole: 'support_staff',
    assignedAt: new Date('2026-01-03T14:00:00'),
    relatedCertificateId: 'cert-001',
    createdBy: 'user-007',
    createdByName: 'Omar Hassan',
    createdAt: new Date('2026-01-03T13:45:00'),
    updatedAt: new Date('2026-01-03T16:30:00'),
    tags: ['certificate', 'physical-copy'],
    attachments: [],
    messageCount: 1,
    slaDeadline: new Date('2026-01-04T13:45:00'),
    responseDeadline: new Date('2026-01-04T13:45:00'),
    firstResponseAt: new Date('2026-01-03T14:10:00'),
  },
  {
    id: 'ticket-006',
    ticketNumber: 'TKT-2026-00006',
    companyId: 'company-002',
    companyName: 'Global Halal Foods Inc',
    type: 'general_inquiry' as TicketType,
    status: 'open' as TicketStatus,
    priority: 'low' as TicketPriority,
    subject: 'Question about renewal process timeline',
    description:
      'Our certificate expires in 3 months. When should we start the renewal process? Do we need a full audit again or is it a simplified process?',
    createdBy: 'user-003',
    createdByName: 'Maria Garcia',
    createdAt: new Date('2026-01-04T07:30:00'),
    updatedAt: new Date('2026-01-04T07:30:00'),
    tags: ['renewal', 'timeline'],
    attachments: [],
    messageCount: 0,
    slaDeadline: new Date('2026-01-05T07:30:00'),
    responseDeadline: new Date('2026-01-05T07:30:00'),
  },
  {
    id: 'ticket-007',
    ticketNumber: 'TKT-2026-00007',
    companyId: 'company-005',
    companyName: 'Spice Trading Company',
    type: 'technical_issue' as TicketType,
    status: 'waiting_internal' as TicketStatus,
    priority: 'high' as TicketPriority,
    subject: 'Cannot access training module',
    description:
      'When I click on "My Training" in the menu, I get a blank page. Tried on Chrome and Safari, same issue. This is blocking our team from completing required training.',
    assignedTo: 'staff-004',
    assignedToName: 'Tech Support Team',
    assignedToRole: 'support_staff',
    assignedAt: new Date('2026-01-03T16:00:00'),
    createdBy: 'user-009',
    createdByName: 'Priya Sharma',
    createdAt: new Date('2026-01-03T15:45:00'),
    updatedAt: new Date('2026-01-03T18:20:00'),
    tags: ['technical', 'training-module'],
    attachments: [],
    messageCount: 2,
    slaDeadline: new Date('2026-01-03T23:45:00'),
    responseDeadline: new Date('2026-01-03T23:45:00'),
    firstResponseAt: new Date('2026-01-03T16:15:00'),
  },
  {
    id: 'ticket-008',
    ticketNumber: 'TKT-2026-00008',
    companyId: 'company-006',
    companyName: 'Premium Meats Ltd',
    type: 'complaint' as TicketType,
    status: 'in_progress' as TicketStatus,
    priority: 'urgent' as TicketPriority,
    subject: 'Audit report contains incorrect information',
    description:
      'The audit report states we use pork-based gelatin in product X, but we have NEVER used any pork products. This is a serious error that could damage our reputation. We need this corrected immediately.',
    assignedTo: 'staff-002',
    assignedToName: 'Ahmed Al-Rahman',
    assignedToRole: 'consultant',
    assignedAt: new Date('2026-01-04T09:00:00'),
    createdBy: 'user-011',
    createdByName: 'Robert Johnson',
    createdAt: new Date('2026-01-04T08:45:00'),
    updatedAt: new Date('2026-01-04T09:30:00'),
    tags: ['audit-report', 'correction-needed'],
    attachments: [],
    messageCount: 1,
    slaDeadline: new Date('2026-01-04T12:45:00'),
    responseDeadline: new Date('2026-01-04T12:45:00'),
    firstResponseAt: new Date('2026-01-04T09:05:00'),
  },
  {
    id: 'ticket-009',
    ticketNumber: 'TKT-2026-00009',
    companyId: 'company-007',
    companyName: 'Sweet Confections Inc',
    type: 'renewal_support' as TicketType,
    status: 'resolved' as TicketStatus,
    priority: 'medium' as TicketPriority,
    subject: 'Renewal application submitted - what are next steps?',
    description:
      'I submitted our renewal application yesterday. Can you confirm it was received and let me know the next steps? Do we need to prepare anything for the audit?',
    assignedTo: 'staff-001',
    assignedToName: 'Sarah Johnson',
    assignedToRole: 'consultant',
    assignedAt: new Date('2026-01-02T10:00:00'),
    createdBy: 'user-013',
    createdByName: 'Emma Wilson',
    createdAt: new Date('2026-01-02T09:30:00'),
    updatedAt: new Date('2026-01-02T16:45:00'),
    resolvedAt: new Date('2026-01-02T16:45:00'),
    tags: ['renewal', 'next-steps'],
    attachments: [],
    messageCount: 3,
    slaDeadline: new Date('2026-01-03T09:30:00'),
    responseDeadline: new Date('2026-01-03T09:30:00'),
    firstResponseAt: new Date('2026-01-02T10:15:00'),
    rating: 5,
    ratingFeedback: 'Very clear explanation. Thank you!',
  },
  {
    id: 'ticket-010',
    ticketNumber: 'TKT-2026-00010',
    companyId: 'company-008',
    companyName: 'Organic Foods Co',
    type: 'feedback' as TicketType,
    status: 'closed' as TicketStatus,
    priority: 'low' as TicketPriority,
    subject: 'Suggestion: Add more training sessions',
    description:
      'The current training schedule only has 2 sessions per month. Would it be possible to add more sessions, especially evening sessions for those of us who work during the day?',
    assignedTo: 'staff-003',
    assignedToName: 'Linda Chen',
    assignedToRole: 'trainer',
    assignedAt: new Date('2025-12-28T11:00:00'),
    createdBy: 'user-015',
    createdByName: 'Michael Chang',
    createdAt: new Date('2025-12-28T10:30:00'),
    updatedAt: new Date('2025-12-29T14:00:00'),
    resolvedAt: new Date('2025-12-29T14:00:00'),
    closedAt: new Date('2025-12-29T14:00:00'),
    tags: ['feedback', 'training-schedule'],
    attachments: [],
    messageCount: 2,
    slaDeadline: new Date('2025-12-30T10:30:00'),
    responseDeadline: new Date('2025-12-30T10:30:00'),
    firstResponseAt: new Date('2025-12-28T11:30:00'),
    rating: 4,
    ratingFeedback: 'Thanks for considering the suggestion',
  },
];

// ===== Ticket Messages =====

export const mockTicketMessages: TicketMessage[] = [
  // Ticket 001 messages
  {
    id: 'msg-001',
    ticketId: 'ticket-001',
    authorId: 'user-001',
    authorName: 'John Smith',
    authorRole: 'company',
    message:
      'I am trying to upload our halal certificate for supplier ABC Corp, but the file upload keeps failing with error "File too large". The file is only 2MB. Can you help?',
    isInternal: false,
    attachments: [],
    createdAt: new Date('2026-01-03T09:15:00'),
  },
  {
    id: 'msg-002',
    ticketId: 'ticket-001',
    authorId: 'staff-001',
    authorName: 'Sarah Johnson',
    authorRole: 'consultant',
    message:
      'Hi John, thank you for reporting this issue. I can see the error in our logs. The file size limit was incorrectly set to 1MB. I have escalated this to our technical team to increase it to 10MB as it should be. You should be able to upload within the next hour.',
    isInternal: false,
    attachments: [],
    createdAt: new Date('2026-01-03T10:30:00'),
  },
  {
    id: 'msg-003',
    ticketId: 'ticket-001',
    authorId: 'staff-001',
    authorName: 'Sarah Johnson',
    authorRole: 'consultant',
    message: 'Technical team confirmed file size limit has been increased to 10MB.',
    isInternal: true,
    attachments: [],
    createdAt: new Date('2026-01-03T11:45:00'),
  },
  {
    id: 'msg-004',
    ticketId: 'ticket-001',
    authorId: 'staff-001',
    authorName: 'Sarah Johnson',
    authorRole: 'consultant',
    message:
      'Good news! The file size limit has been corrected. Please try uploading again. Let me know if you encounter any issues.',
    isInternal: false,
    attachments: [],
    createdAt: new Date('2026-01-03T14:20:00'),
  },

  // Ticket 002 messages
  {
    id: 'msg-005',
    ticketId: 'ticket-002',
    authorId: 'user-003',
    authorName: 'Maria Garcia',
    authorRole: 'company',
    message:
      'Regarding item #12 on the pre-audit checklist about ingredient sourcing documentation - do we need certificates for every single ingredient or just animal-derived ones?',
    isInternal: false,
    attachments: [],
    createdAt: new Date('2026-01-02T10:45:00'),
  },
  {
    id: 'msg-006',
    ticketId: 'ticket-002',
    authorId: 'staff-002',
    authorName: 'Ahmed Al-Rahman',
    authorRole: 'consultant',
    message:
      'Hi Maria, excellent question! You need halal certificates for:\n\n1. ALL animal-derived ingredients (meat, gelatin, animal fats, etc.)\n2. Ingredients that MAY contain animal derivatives (emulsifiers, enzymes, flavorings)\n3. Alcohol-based ingredients or those processed with alcohol\n\nFor plant-based ingredients with no risk of cross-contamination (rice, vegetables, pure spices), certificates are not required but supplier declarations are recommended.\n\nWould you like me to review your ingredient list to identify which ones specifically need certificates?',
    isInternal: false,
    attachments: [],
    createdAt: new Date('2026-01-02T14:20:00'),
  },

  // Ticket 003 messages
  {
    id: 'msg-007',
    ticketId: 'ticket-003',
    authorId: 'user-005',
    authorName: 'David Lee',
    authorRole: 'company',
    message:
      'I missed the December 15 training session on Halal Standards. Is there a recording available?',
    isInternal: false,
    attachments: [],
    createdAt: new Date('2025-12-20T08:30:00'),
  },
  {
    id: 'msg-008',
    ticketId: 'ticket-003',
    authorId: 'staff-003',
    authorName: 'Linda Chen',
    authorRole: 'trainer',
    message:
      'Hi David, yes we do have the recording! Here is the link: https://training.halmas.com/recordings/halal-standards-dec15\n\nThe password is: HalalStandards2025\n\nThe recording will be available for 30 days. Let me know if you have any questions about the content!',
    isInternal: false,
    attachments: [],
    createdAt: new Date('2025-12-20T09:10:00'),
  },

  // Ticket 005 messages
  {
    id: 'msg-009',
    ticketId: 'ticket-005',
    authorId: 'user-007',
    authorName: 'Omar Hassan',
    authorRole: 'company',
    message:
      'We need 3 more physical copies of our halal certificate (CERT-2025-001) to display in our new branch locations.',
    isInternal: false,
    attachments: [],
    createdAt: new Date('2026-01-03T13:45:00'),
  },
  {
    id: 'msg-010',
    ticketId: 'ticket-005',
    authorId: 'staff-001',
    authorName: 'Sarah Johnson',
    authorRole: 'support_staff',
    message:
      'Hi Omar, I can help with that! To process additional certificate copies, I need:\n\n1. Delivery address for each copy\n2. Contact person at each location\n3. Preferred delivery method (standard mail or courier)\n\nThe cost is $25 per certified copy. Shall I prepare a quote?',
    isInternal: false,
    attachments: [],
    createdAt: new Date('2026-01-03T14:10:00'),
  },
];

// ===== Knowledge Base Articles =====

export const mockKnowledgeBaseArticles: KnowledgeBaseArticle[] = [
  {
    id: 'article-001',
    slug: 'getting-started-with-halal-certification',
    category: 'getting_started' as KnowledgeBaseCategory,
    title: 'Getting Started with Halal Certification',
    excerpt:
      'A comprehensive guide to understanding the halal certification process, requirements, and timeline.',
    content: `
# Getting Started with Halal Certification

Halal certification is a process that ensures your food products comply with Islamic dietary laws. This guide will help you understand the basics and get started.

## What is Halal Certification?

Halal certification is an official document issued by an accredited certifying body that confirms your products meet halal standards as defined by Islamic law (Shariah).

## Who Needs Halal Certification?

- Food manufacturers
- Restaurants and catering services
- Food importers and exporters
- Ingredient suppliers
- Food service companies

## Basic Requirements

1. **Halal Ingredients**: All ingredients must be halal-compliant
2. **Clean Production**: No cross-contamination with haram (forbidden) substances
3. **Proper Storage**: Separate storage from non-halal products
4. **Documentation**: Complete traceability of all ingredients

## The Certification Process

### Step 1: Application
Submit your application through our online portal with basic company information.

### Step 2: Documentation Review
Our team reviews your product formulations, ingredient specifications, and production processes.

### Step 3: On-Site Audit
An auditor visits your facility to verify compliance with halal standards.

### Step 4: Certification
Upon successful audit, your halal certificate is issued (valid for 1 year).

## Timeline

- Application review: 3-5 business days
- Audit scheduling: 1-2 weeks
- Audit completion: 1 day
- Certificate issuance: 3-5 business days after successful audit

**Total estimated time: 3-4 weeks**

## Next Steps

1. [Create an account](/register)
2. [Start your application](/company/applications/new)
3. [Review the checklist](/company/support/articles/pre-application-checklist)
4. [Book a consultation](/company/support/tickets/new)

For questions, contact our support team or browse our FAQ section.
    `,
    tags: ['getting-started', 'certification-process', 'requirements'],
    author: 'HALMAS Team',
    publishedAt: new Date('2025-11-01T00:00:00'),
    updatedAt: new Date('2025-12-15T00:00:00'),
    viewCount: 1247,
    helpful: 89,
    notHelpful: 3,
    featured: true,
    relatedArticles: ['article-002', 'article-003'],
  },
  {
    id: 'article-002',
    slug: 'pre-application-checklist',
    category: 'application_process' as KnowledgeBaseCategory,
    title: 'Pre-Application Checklist',
    excerpt: 'Essential documents and information you need before starting your halal certification application.',
    content: `
# Pre-Application Checklist

Before starting your halal certification application, make sure you have the following documents and information ready.

## Company Information

- [ ] Business registration certificate
- [ ] Trade license
- [ ] Company profile
- [ ] List of all production facilities

## Product Information

- [ ] Complete list of products to be certified
- [ ] Product formulations for each item
- [ ] Ingredient specifications with suppliers
- [ ] Product flow charts

## Ingredient Documentation

For each ingredient, provide:
- [ ] Halal certificate (if from Muslim countries)
- [ ] Halal certificate (if animal-derived)
- [ ] Technical specification sheet
- [ ] Supplier information

## Facility Information

- [ ] Floor plan of production facility
- [ ] Photos of production areas
- [ ] Equipment list
- [ ] Cleaning procedures
- [ ] Pest control procedures

## Quality & Safety

- [ ] HACCP certification (if applicable)
- [ ] ISO certification (if applicable)
- [ ] Food safety license
- [ ] Health inspection reports

## Having Trouble?

If you're missing any documents, don't worry! You can still start your application and upload documents as you gather them. Our consultants can also help guide you through the process.

[Start Your Application](/company/applications/new)
    `,
    tags: ['checklist', 'application', 'documents'],
    author: 'Sarah Johnson',
    publishedAt: new Date('2025-11-05T00:00:00'),
    updatedAt: new Date('2025-12-10T00:00:00'),
    viewCount: 892,
    helpful: 67,
    notHelpful: 2,
    featured: true,
    relatedArticles: ['article-001', 'article-003'],
  },
  {
    id: 'article-003',
    slug: 'understanding-halal-ingredients',
    category: 'audit_preparation' as KnowledgeBaseCategory,
    title: 'Understanding Halal Ingredients',
    excerpt: 'Learn which ingredients are halal, which require certification, and how to verify ingredient sources.',
    content: `
# Understanding Halal Ingredients

Not all ingredients require halal certification, but knowing which ones do is crucial for compliance.

## Always Halal (No Certificate Needed)

- Pure plant ingredients (vegetables, fruits, grains)
- Water, salt, sugar
- Pure spices and herbs
- Vegetable oils (palm, sunflower, olive, etc.)

## Always Require Halal Certificate

### Animal-Derived Ingredients
- Meat and poultry
- Gelatin
- Animal fats (lard, tallow)
- Dairy products
- Eggs

### May Contain Animal Derivatives
- Emulsifiers (E471, E472)
- Mono and diglycerides
- Enzymes
- Flavorings
- Vitamins (D3, B12)
- L-cysteine

## Questionable Ingredients

Some ingredients may be from halal or non-halal sources. Always verify:

- **Enzymes**: Can be from animal, plant, or microbial sources
- **Glycerin**: Can be from animal fats or vegetable oils
- **Lecithin**: Usually from soy (halal) but can be from egg
- **Rennet**: For cheese - must be microbial or vegetable-based

## Alcohol in Food Products

- **Ethyl alcohol**: Not permitted even in small amounts
- **Naturally occurring alcohol**: In vinegar, soy sauce - generally accepted if < 0.5%

## How to Verify

1. Request halal certificate from supplier
2. Check ingredient specification sheet
3. Contact supplier for source clarification
4. Consult with your assigned consultant

## Red Flags

Watch out for these ingredients that are never halal:
- Pork and pork derivatives
- Alcohol (as an ingredient)
- Gelatin from non-halal sources
- Animal shortening from non-halal sources
- Whey from non-halal rennet

Need help? [Contact our team](/company/support/tickets/new)
    `,
    tags: ['ingredients', 'halal-standards', 'certification-requirements'],
    author: 'Ahmed Al-Rahman',
    publishedAt: new Date('2025-11-10T00:00:00'),
    updatedAt: new Date('2025-12-01T00:00:00'),
    viewCount: 756,
    helpful: 54,
    notHelpful: 1,
    featured: true,
    relatedArticles: ['article-001', 'article-002'],
  },
  {
    id: 'article-004',
    slug: 'preparing-for-audit-inspection',
    category: 'audit_preparation' as KnowledgeBaseCategory,
    title: 'Preparing for Your Audit Inspection',
    excerpt: 'A comprehensive guide to help you prepare for your on-site halal audit inspection.',
    content: `
# Preparing for Your Audit Inspection

The on-site audit is a critical step in obtaining your halal certification. Here's how to prepare.

## Before the Audit

### 1 Week Before
- Review all documentation with your team
- Conduct internal inspection of facilities
- Ensure all required certificates are current
- Brief all staff on audit procedures

### 3 Days Before
- Deep clean all production areas
- Organize documentation in folders
- Prepare tour route through facility
- Test all equipment

### 1 Day Before
- Final facility walkthrough
- Verify all documents are accessible
- Prepare meeting room for auditor
- Confirm audit schedule with team

## During the Audit

### Opening Meeting
The auditor will:
- Introduce themselves and explain the process
- Review audit scope and schedule
- Answer any questions you have

### Facility Tour
Be prepared to show:
- Raw material storage areas
- Production floor
- Packaging areas
- Finished goods storage
- Quality control lab (if applicable)

### Document Review
Have these readily available:
- Product formulations
- Ingredient certificates
- Cleaning procedures
- Training records
- Supplier information

### Staff Interviews
Auditors may ask staff about:
- Halal procedures
- Ingredient handling
- Cleaning protocols
- Their training

## Common Issues to Avoid

❌ Missing ingredient certificates
❌ Incomplete cleaning logs
❌ Staff unfamiliar with halal procedures
❌ Poor facility organization
❌ Inadequate ingredient segregation

## After the Audit

### Closing Meeting
The auditor will:
- Present preliminary findings
- Discuss any non-conformances
- Explain next steps

### Non-Conformances
If issues are found:
- Minor issues: Can be corrected quickly
- Major issues: May require corrective action plan
- Critical issues: May delay certification

### Timeline
- Audit report: 2-3 business days
- Certificate issuance: 3-5 days after clean audit

## Tips for Success

✅ Be transparent and honest
✅ Have a designated person to accompany auditor
✅ Take notes during the audit
✅ Ask questions if anything is unclear
✅ Stay calm and professional

Questions? [Contact your consultant](/company/support/tickets/new)
    `,
    tags: ['audit', 'preparation', 'inspection'],
    author: 'Linda Chen',
    publishedAt: new Date('2025-11-15T00:00:00'),
    updatedAt: new Date('2025-12-20T00:00:00'),
    viewCount: 634,
    helpful: 48,
    notHelpful: 2,
    featured: false,
    relatedArticles: ['article-003', 'article-002'],
  },
  {
    id: 'article-005',
    slug: 'understanding-corrective-action-reports',
    category: 'audit_preparation' as KnowledgeBaseCategory,
    title: 'Understanding Corrective Action Reports (CAR)',
    excerpt: 'What to do when your audit identifies issues that need correction.',
    content: `
# Understanding Corrective Action Reports (CAR)

If your audit finds non-conformances, you'll receive a Corrective Action Report. Here's what you need to know.

## What is a CAR?

A Corrective Action Report documents issues found during your audit that must be addressed before certification can be issued.

## Types of Non-Conformances

### Minor
- Small documentation gaps
- Procedural improvements needed
- Training gaps
**Typical timeline:** 2-7 days to correct

### Major
- Missing critical certificates
- Inadequate segregation
- Incomplete procedures
**Typical timeline:** 1-2 weeks to correct

### Critical
- Haram ingredients found
- Cross-contamination risk
- False documentation
**Typical timeline:** May require re-audit

## CAR Response Process

1. **Receive CAR** - Detailed report of findings
2. **Root Cause Analysis** - Identify why issue occurred
3. **Corrective Action** - Fix the immediate problem
4. **Preventive Action** - Ensure it doesn't happen again
5. **Evidence Submission** - Prove corrections were made
6. **Review** - Auditor reviews your response

## What to Include in Your Response

For each finding:
- ✅ Description of what you did to fix it
- ✅ Evidence (photos, documents, certificates)
- ✅ Preventive measures implemented
- ✅ Timeline of actions taken

## Common CAR Items

### Documentation Issues
- Missing supplier certificates → Request from suppliers
- Incomplete procedures → Update and document
- Training records gaps → Conduct training sessions

### Facility Issues
- Inadequate labeling → Re-label all areas
- Poor segregation → Install physical barriers
- Cleaning gaps → Update cleaning schedule

### Ingredient Issues
- Questionable ingredients → Get halal certificates
- Missing traceability → Implement tracking system

## Example CAR Response

**Finding:** "Missing halal certificate for emulsifier E471"

**Response:**
1. Root cause: Supplier changed formula without notification
2. Immediate action: Contacted supplier, received halal certificate (attached)
3. Preventive action: Implemented quarterly supplier certificate review
4. Evidence: New certificate valid until Dec 2026 (see attachment)

## Timeline Expectations

- CAR issued: Within 2-3 days of audit
- Response due: 7-14 days (varies by severity)
- Review time: 2-5 days
- Follow-up audit: Only if critical issues

## Tips for Success

✅ Respond quickly - don't wait until the deadline
✅ Be thorough - address root causes, not just symptoms
✅ Provide evidence - photos, certificates, updated documents
✅ Ask for help - your consultant can guide you
✅ Be honest - transparency builds trust

## Need Help?

Your assigned consultant can help you:
- Understand the findings
- Develop corrective actions
- Gather required evidence
- Submit your response

[Contact Support](/company/support/tickets/new)
    `,
    tags: ['CAR', 'corrective-action', 'non-conformance'],
    author: 'Ahmed Al-Rahman',
    publishedAt: new Date('2025-11-20T00:00:00'),
    updatedAt: new Date('2025-12-18T00:00:00'),
    viewCount: 512,
    helpful: 41,
    notHelpful: 1,
    featured: false,
    relatedArticles: ['article-004', 'article-003'],
  },
  {
    id: 'article-006',
    slug: 'certificate-renewal-process',
    category: 'renewal' as KnowledgeBaseCategory,
    title: 'Certificate Renewal Process',
    excerpt: 'Everything you need to know about renewing your halal certification.',
    content: `
# Certificate Renewal Process

Halal certificates are valid for one year. Here's how to renew yours.

## When to Start

Start your renewal process **60-90 days before expiration**. This ensures:
- Adequate time for renewal audit
- No gap in certification
- Better audit scheduling options

## Renewal vs. Initial Certification

### Similarities
- On-site audit required
- Documentation review
- Facility inspection

### Differences
- ✅ Faster application review
- ✅ Auditor familiar with your facility
- ✅ Focus on changes since last audit
- ✅ Often shorter audit duration

## Required Updates

Provide information on any changes since last certification:

### Product Changes
- New products added
- Formulation changes
- Discontinued products

### Supplier Changes
- New suppliers
- Updated certificates
- Changed ingredients

### Facility Changes
- Equipment upgrades
- Layout modifications
- New production lines

### Process Changes
- Updated procedures
- New quality systems
- Staff changes

## Renewal Application

Submit through your account:
1. Update company information
2. List any changes (see above)
3. Upload updated certificates
4. Pay renewal fee

## Renewal Audit

### What's Different
- Focus on changes and updates
- Review of previous CAR items
- Shorter duration (typically 2-4 hours vs. full day)

### What's the Same
- Full facility tour
- Document review
- Staff interviews
- Compliance verification

## Timeline

- Application submission: Day 1
- Review and audit scheduling: 3-5 days
- Renewal audit: Scheduled within 1-2 weeks
- Certificate issuance: 3-5 days after audit

**Total time: 3-4 weeks**

## Costs

Renewal fees are typically:
- 70-80% of initial certification cost
- Depends on number of products
- Additional fees for major changes

## What If I'm Late?

### Less than 30 days expired
- Standard renewal process
- Small late fee may apply

### 30-90 days expired
- Extended review required
- Higher fees
- May need more comprehensive audit

### More than 90 days expired
- Treated as new application
- Full certification process
- Full fees apply

## Tips for Smooth Renewal

✅ Start early (60-90 days before expiration)
✅ Keep documentation updated throughout the year
✅ Maintain good communication with consultant
✅ Track any changes as they occur
✅ Budget for renewal costs in advance

## Automatic Reminders

You'll receive renewal reminders:
- 90 days before expiration
- 60 days before expiration
- 30 days before expiration
- 14 days before expiration

[Start Your Renewal](/company/applications/new?type=renewal)
    `,
    tags: ['renewal', 'certificate', 'expiration'],
    author: 'Sarah Johnson',
    publishedAt: new Date('2025-10-15T00:00:00'),
    updatedAt: new Date('2025-12-28T00:00:00'),
    viewCount: 423,
    helpful: 35,
    notHelpful: 0,
    featured: false,
    relatedArticles: ['article-001'],
  },
  {
    id: 'article-007',
    slug: 'payment-and-invoicing-guide',
    category: 'payments_invoices' as KnowledgeBaseCategory,
    title: 'Payment and Invoicing Guide',
    excerpt: 'Understanding payment terms, methods, and how to manage your invoices.',
    content: `
# Payment and Invoicing Guide

Learn about payment options, invoice management, and payment schedules.

## Payment Schedule

### Initial Certification
1. **Application fee** - Due at application submission (non-refundable)
2. **Audit fee** - Due before audit scheduling
3. **Certificate fee** - Due after successful audit

### Renewal
- Single payment due at renewal application

## Payment Methods

We accept:
- 💳 Credit/Debit Card (Visa, Mastercard, Amex)
- 🏦 Bank Transfer
- 💰 PayPal
- 📧 Wire Transfer (international)

### Processing Times
- Credit card: Instant
- Bank transfer: 1-3 business days
- Wire transfer: 3-5 business days

## Invoice Management

### Viewing Invoices
Access all invoices in your account:
1. Go to [Payment Dashboard](/company/payments)
2. View list of all invoices
3. Download PDF copies

### Invoice Details
Each invoice includes:
- Invoice number
- Issue date
- Due date
- Line items
- Payment instructions
- Tax information (if applicable)

## Payment Status

### Pending
- Invoice issued, payment not yet received
- Payment due within terms (typically 14 days)

### Processing
- Payment received, verifying with bank
- Usually clears within 1-3 days

### Paid
- Payment confirmed and applied
- Receipt available for download

### Overdue
- Payment not received by due date
- Late fees may apply after 14 days

## Late Payments

If your payment is overdue:
- Day 1-14: Reminder emails sent
- Day 15+: Late fee applied (10% of invoice)
- Day 30+: Certification process may be paused

**To avoid issues:** Set up payment reminders or pay early!

## Payment Issues

### Payment Not Reflected
If you made a payment but status shows pending:
1. Allow 1-3 business days for processing
2. Check payment confirmation email
3. [Contact support](/company/support/tickets/new?type=payment_issue) with:
   - Invoice number
   - Payment date
   - Transaction reference
   - Payment method

### Disputed Charges
To dispute an invoice:
1. [Open a support ticket](/company/support/tickets/new)
2. Include invoice number
3. Explain the issue
4. Provide supporting documentation

Response time: 1-2 business days

## Refund Policy

### Application Fee
- Non-refundable once application is reviewed

### Audit Fee
- Full refund if cancelled 7+ days before audit
- 50% refund if cancelled 3-6 days before
- No refund if cancelled < 3 days before

### Certificate Fee
- Full refund if certification is denied (not due to your fault)

## Tax Information

### VAT/Sales Tax
- Applied based on your location
- Rate varies by jurisdiction
- Shown separately on invoice

### Tax Receipts
Download tax-compliant receipts from your payment dashboard.

## Questions?

For payment support:
- [Create a ticket](/company/support/tickets/new?type=payment_issue)
- Email: payments@halmas.com
- Phone: +1 (555) 123-4567

[View Your Invoices](/company/payments)
    `,
    tags: ['payment', 'invoice', 'billing'],
    author: 'HALMAS Finance Team',
    publishedAt: new Date('2025-11-25T00:00:00'),
    updatedAt: new Date('2026-01-02T00:00:00'),
    viewCount: 387,
    helpful: 31,
    notHelpful: 1,
    featured: false,
    relatedArticles: [],
  },
  {
    id: 'article-008',
    slug: 'training-requirements-guide',
    category: 'training' as KnowledgeBaseCategory,
    title: 'Halal Training Requirements',
    excerpt: 'Understanding mandatory training for halal certification and compliance.',
    content: `
# Halal Training Requirements

Proper training is essential for maintaining halal compliance. Here's what you need to know.

## Who Needs Training?

### Required Training
- Production managers
- Quality control staff
- Purchasing managers
- Anyone handling ingredients

### Recommended Training
- All production staff
- Warehouse staff
- Administrative staff involved in certification

## Training Modules

### 1. Halal Fundamentals (Required)
**Duration:** 2 hours
**Topics:**
- Islamic dietary laws
- Halal vs. haram ingredients
- Critical control points
- Documentation requirements

### 2. Ingredient Verification (Required for QC)
**Duration:** 1.5 hours
**Topics:**
- Reading ingredient certificates
- Verifying supplier compliance
- Managing ingredient changes
- Traceability systems

### 3. Production Best Practices (Recommended)
**Duration:** 2 hours
**Topics:**
- Preventing cross-contamination
- Cleaning procedures
- Equipment dedication
- Record keeping

### 4. Audit Preparation (Recommended)
**Duration:** 1 hour
**Topics:**
- What auditors look for
- Documentation preparation
- Facility readiness
- Common pitfalls

## Training Schedule

We offer sessions:
- **Live online:** 2x per month
- **On-demand:** Available 24/7
- **On-site:** Custom sessions for teams of 10+

[View Training Schedule](/company/training)

## Certification

Upon completing each module:
- ✅ Take assessment test (80% to pass)
- ✅ Receive digital certificate
- ✅ Certificate valid for 1 year
- ✅ Certificates stored in your account

## Training Records

Auditors will review:
- List of trained staff
- Training certificates
- Refresher training records
- Attendance records

Keep these readily available!

## Refresher Training

Required annually for:
- All previously trained staff
- Staff with new responsibilities
- When procedures change significantly

## Team Training

For teams of 10 or more:
- Custom on-site sessions
- Flexible scheduling
- Discounted group rates
- Customized content

[Request Team Training](/company/support/tickets/new?type=training_support)

## Training Costs

- Individual session: $50 per person
- Annual unlimited: $200 per person
- Team packages: Contact for pricing

## Tips for Success

✅ Train staff before audit
✅ Keep certificates organized
✅ Schedule refreshers in advance
✅ Designate a training coordinator
✅ Use on-demand modules for new hires

[Enroll in Training](/company/training)
    `,
    tags: ['training', 'requirements', 'compliance'],
    author: 'Linda Chen',
    publishedAt: new Date('2025-12-01T00:00:00'),
    updatedAt: new Date('2025-12-30T00:00:00'),
    viewCount: 298,
    helpful: 24,
    notHelpful: 0,
    featured: false,
    relatedArticles: ['article-004'],
  },
];

// ===== FAQs =====

export const mockFAQs: FAQ[] = [
  {
    id: 'faq-001',
    category: 'getting_started' as KnowledgeBaseCategory,
    question: 'How long does the halal certification process take?',
    answer:
      'The typical timeline is 3-4 weeks from application submission to certificate issuance. This includes: application review (3-5 days), audit scheduling (1-2 weeks), on-site audit (1 day), and certificate issuance (3-5 days after successful audit).',
    order: 1,
    helpful: 45,
    notHelpful: 2,
    relatedArticles: ['article-001'],
  },
  {
    id: 'faq-002',
    category: 'getting_started' as KnowledgeBaseCategory,
    question: 'How much does halal certification cost?',
    answer:
      'Certification costs vary based on company size, number of products, and facility complexity. Small businesses (1-5 products) typically pay $500-1500, medium businesses (6-20 products) $1500-3500, and large manufacturers (20+ products) $3500+. Contact us for a detailed quote.',
    order: 2,
    helpful: 38,
    notHelpful: 1,
    relatedArticles: ['article-001'],
  },
  {
    id: 'faq-003',
    category: 'application_process' as KnowledgeBaseCategory,
    question: 'Can I start my application if I don\'t have all documents ready?',
    answer:
      'Yes! You can submit your initial application and upload documents as you gather them. Our team will review what you have and guide you on any missing items. However, the audit cannot be scheduled until all required documents are submitted.',
    order: 1,
    helpful: 29,
    notHelpful: 0,
    relatedArticles: ['article-002'],
  },
  {
    id: 'faq-004',
    category: 'audit_preparation' as KnowledgeBaseCategory,
    question: 'Do all ingredients need halal certificates?',
    answer:
      'No. Pure plant-based ingredients (vegetables, fruits, grains) and simple compounds (salt, sugar, water) do not require certificates. However, animal-derived ingredients, ingredients that may contain animal derivatives (emulsifiers, enzymes), and alcohol-based ingredients DO require halal certification.',
    order: 1,
    helpful: 51,
    notHelpful: 1,
    relatedArticles: ['article-003'],
  },
  {
    id: 'faq-005',
    category: 'certificates' as KnowledgeBaseCategory,
    question: 'How long is the halal certificate valid?',
    answer:
      'Halal certificates are valid for 1 year from the date of issuance. You will receive renewal reminders 90 days, 60 days, and 30 days before expiration. The renewal process is faster than initial certification.',
    order: 1,
    helpful: 33,
    notHelpful: 0,
    relatedArticles: [],
  },
];

// ===== Helper Functions =====

/**
 * Get ticket by ID
 */
export function getTicketById(id: string): SupportTicket | undefined {
  return mockTickets.find((t) => t.id === id);
}

/**
 * Get tickets with filters
 */
export function getTickets(filters?: {
  type?: TicketType;
  status?: TicketStatus;
  priority?: TicketPriority;
  assignedTo?: string;
  companyId?: string;
  search?: string;
}): SupportTicket[] {
  let filtered = [...mockTickets];

  if (filters?.type) {
    filtered = filtered.filter((t) => t.type === filters.type);
  }

  if (filters?.status) {
    filtered = filtered.filter((t) => t.status === filters.status);
  }

  if (filters?.priority) {
    filtered = filtered.filter((t) => t.priority === filters.priority);
  }

  if (filters?.assignedTo) {
    if (filters.assignedTo === 'unassigned') {
      filtered = filtered.filter((t) => !t.assignedTo);
    } else {
      filtered = filtered.filter((t) => t.assignedTo === filters.assignedTo);
    }
  }

  if (filters?.companyId) {
    filtered = filtered.filter((t) => t.companyId === filters.companyId);
  }

  if (filters?.search) {
    const query = filters.search.toLowerCase();
    filtered = filtered.filter(
      (t) =>
        t.ticketNumber.toLowerCase().includes(query) ||
        t.subject.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.companyName.toLowerCase().includes(query)
    );
  }

  // Sort by created date (newest first)
  return filtered.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

/**
 * Get ticket messages
 */
export function getTicketMessages(ticketId: string): TicketMessage[] {
  return mockTicketMessages
    .filter((m) => m.ticketId === ticketId)
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

/**
 * Get knowledge base articles
 */
export function getKnowledgeBaseArticles(category?: KnowledgeBaseCategory): KnowledgeBaseArticle[] {
  if (category) {
    return mockKnowledgeBaseArticles.filter((a) => a.category === category);
  }
  return [...mockKnowledgeBaseArticles];
}

/**
 * Get article by slug
 */
export function getArticleBySlug(slug: string): KnowledgeBaseArticle | undefined {
  return mockKnowledgeBaseArticles.find((a) => a.slug === slug);
}

/**
 * Get FAQs
 */
export function getFAQs(category?: KnowledgeBaseCategory): FAQ[] {
  let faqs = [...mockFAQs];

  if (category) {
    faqs = faqs.filter((f) => f.category === category);
  }

  return faqs.sort((a, b) => a.order - b.order);
}

/**
 * Get support statistics
 */
export function getSupportStats(): SupportStats {
  const totalTickets = mockTickets.length;
  const openTickets = mockTickets.filter((t) => t.status === 'open').length;
  const inProgressTickets = mockTickets.filter((t) => t.status === 'in_progress').length;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const resolvedToday = mockTickets.filter((t) => {
    if (!t.resolvedAt) return false;
    const resolvedDate = new Date(t.resolvedAt);
    resolvedDate.setHours(0, 0, 0, 0);
    return resolvedDate.getTime() === today.getTime();
  }).length;

  // Calculate avg response time (in hours)
  const ticketsWithResponse = mockTickets.filter((t) => t.firstResponseAt);
  const avgResponseTime =
    ticketsWithResponse.length > 0
      ? ticketsWithResponse.reduce((sum, t) => {
          const responseTime =
            (new Date(t.firstResponseAt!).getTime() - new Date(t.createdAt).getTime()) /
            (1000 * 60 * 60);
          return sum + responseTime;
        }, 0) / ticketsWithResponse.length
      : 0;

  // Calculate avg resolution time (in hours)
  const resolvedTickets = mockTickets.filter((t) => t.resolvedAt);
  const avgResolutionTime =
    resolvedTickets.length > 0
      ? resolvedTickets.reduce((sum, t) => {
          const resolutionTime =
            (new Date(t.resolvedAt!).getTime() - new Date(t.createdAt).getTime()) /
            (1000 * 60 * 60);
          return sum + resolutionTime;
        }, 0) / resolvedTickets.length
      : 0;

  // Calculate satisfaction rating
  const ratedTickets = mockTickets.filter((t) => t.rating);
  const satisfactionRating =
    ratedTickets.length > 0
      ? ratedTickets.reduce((sum, t) => sum + (t.rating || 0), 0) / ratedTickets.length
      : 0;

  // Count tickets by type
  const ticketsByType = mockTickets.reduce((acc, t) => {
    acc[t.type] = (acc[t.type] || 0) + 1;
    return acc;
  }, {} as Record<TicketType, number>);

  // Count tickets by priority
  const ticketsByPriority = mockTickets.reduce((acc, t) => {
    acc[t.priority] = (acc[t.priority] || 0) + 1;
    return acc;
  }, {} as Record<TicketPriority, number>);

  return {
    totalTickets,
    openTickets,
    inProgressTickets,
    resolvedToday,
    avgResponseTime,
    avgResolutionTime,
    satisfactionRating,
    ticketsByType,
    ticketsByPriority,
  };
}
